import type { WizardRace } from "../../../../lib/raceSources";
import type { PlanSummary, TrainingPlanLibrary } from "../../../../lib/trainingPlanApi";
import { LucideIcon } from "../../../FG/LucideIcon";
import type { AboutYouFuel } from "../../profileSteps/useAboutYouFuel";
import type { ReviewRow, SummaryWarning } from "../../rollingReview";
import { formatYmdWeekdayShort } from "../formatting";
import { phaseSegments } from "../phases";
import type { WizardStepId } from "../wizardMeta";
import type { WizardTrainingChoice } from "./MethodStep";

/** BOTH review panels: the no-race review (mock 1g — builds the first box of a
 *  rolling journey) and the race review (journey preview + recap + race-day
 *  card). The caller gates on `step === "review"`; the split stays in here. */
export function ReviewStep({
  noRace,
  reviewRows,
  trainingWarnings,
  setStep,
  library,
  isBuild,
  trainingChoice,
  noPlansForDistance,
  trainingMethodLabel,
  plan,
  dropWindows,
  dropWeeks,
  selectedRace,
  goalLabel,
  recapPlanSummary,
  recapFuelingSummary,
  projectedRaceCarbs,
  projectedRaceTotal,
  fuel,
  applyError,
  overlapUnresolved
}: {
  /** Q1 "Not right now" — the rolling-journey review. */
  noRace: boolean;
  reviewRows: ReviewRow[];
  /** Guardrail 5: an unfuelable calendar (no sessions / no durations) is never silent. */
  trainingWarnings: SummaryWarning[];
  setStep: (step: WizardStepId) => void;
  library: TrainingPlanLibrary | null;
  isBuild: boolean;
  /** Training-plan answer + no-plan distance — choose the plan-less lede. */
  trainingChoice: WizardTrainingChoice;
  noPlansForDistance: boolean;
  /** Training recap line; null = the training question was skipped (D18 —
   *  upcoming workouts), so the row reads the calendar and isn't editable. */
  trainingMethodLabel: string | null;
  plan: PlanSummary | null;
  dropWindows: Array<[number, number]>;
  dropWeeks: number;
  selectedRace: WizardRace | null;
  goalLabel: string | null;
  recapPlanSummary: string;
  recapFuelingSummary: string;
  projectedRaceCarbs: number;
  projectedRaceTotal: number;
  fuel: AboutYouFuel;
  applyError: string | null;
  overlapUnresolved: boolean;
}) {
  return (
    <>
      {/* ── No-race REVIEW (mock 1g) ─────────────────────────────────────── */}
      {noRace && (
        <div className="fgp-wizard-panel">
          {trainingWarnings.map((w) => (
            <div key={w.key} className="fgp-modal-callout fgp-modal-callout--amber" role="note">
              {w.text}
            </div>
          ))}
          <div className="fgp-wz-recap">
            <div className="fgp-wz-recap__head">Your setup — tap anything to change it</div>
            {reviewRows.map((row) =>
              row.stepId === "method" && trainingMethodLabel == null ? (
                // Training question skipped (D18) — nothing to jump back to.
                <div key={row.label} className="fgp-wz-recap__row fgp-wz-recap__row--static">
                  <span className="fgp-wz-recap__k">{row.label}</span>
                  <span className="fgp-wz-recap__v">{row.value}</span>
                </div>
              ) : (
                <button
                  key={row.label}
                  type="button"
                  className="fgp-wz-recap__row"
                  onClick={() => setStep(row.stepId)}>
                  <span className="fgp-wz-recap__k">{row.label}</span>
                  <span className="fgp-wz-recap__v">{row.value} ✎</span>
                </button>
              )
            )}
          </div>
          <p className="fgp-wizard-race-hint">
            Takes a few seconds. You&apos;ll see every product and can change anything before
            ordering.
          </p>
          {applyError && (
            <div className="fgp-modal-callout fgp-modal-callout--error">{applyError}</div>
          )}
        </div>
      )}

      {library && !noRace && (
        <div className="fgp-wizard-panel">
          <p className="fgp-wizard-race-hint" style={{ marginTop: 0 }}>
            {isBuild && trainingChoice === "connector" ? (
              <>
                We&apos;ll fuel the workouts your training app sends —{" "}
                <strong style={{ color: "var(--fg-ink)" }}>connect it after setup</strong> and
                they&apos;ll land on your calendar.
              </>
            ) : isBuild && noPlansForDistance ? (
              <>
                We don&apos;t have a training plan for this distance yet —{" "}
                <strong style={{ color: "var(--fg-ink)" }}>add your own workouts</strong> and we&apos;ll
                build fuel boxes around them.
              </>
            ) : isBuild ? (
              <>
                You&apos;re building your own training —{" "}
                <strong style={{ color: "var(--fg-ink)" }}>add workouts to your calendar</strong> and
                we&apos;ll build fuel boxes around them.
              </>
            ) : (
              <>
                Training lands on your calendar today. Fuel comes in{" "}
                <strong style={{ color: "var(--fg-ink)" }}>{dropWindows.length} boxes</strong>
                {` (every ${dropWeeks === 1 ? "week" : `${dropWeeks} weeks`})`}{" "}
                — you generate each one when you&apos;re ready.
              </>
            )}
          </p>

          {/* ── Journey preview: phase bar + stat tiles (prototype step 6) ── */}
          {!isBuild && plan &&
            (() => {
              const segs = phaseSegments(plan.weekly);
              const segTotal = segs.reduce((s, x) => s + x.weeks, 0) || 1;
              const totalWorkouts = Math.round(plan.stats.runDaysPerWeek * plan.weeks);
              return (
                <div className="fgp-wz-journey">
                  <div className="fgp-wz-jphasebar">
                    {segs.map((s) => (
                      <span key={s.label} style={{ width: `${(s.weeks / segTotal) * 100}%`, background: s.color }} />
                    ))}
                  </div>
                  <div className="fgp-wz-jphaselabels">
                    {segs.map((s) => (
                      <span key={s.label} style={{ width: `${(s.weeks / segTotal) * 100}%` }}>
                        {s.label === "Race" ? "🏁" : s.label}
                      </span>
                    ))}
                  </div>
                  <div className="fgp-wz-stattiles">
                    <div className="fgp-wz-stattile">
                      <div className="fgp-wz-stattile__v">{totalWorkouts}</div>
                      <div className="fgp-wz-stattile__l">workouts</div>
                    </div>
                    <div className="fgp-wz-stattile">
                      <div className="fgp-wz-stattile__v">{dropWindows.length}</div>
                      <div className="fgp-wz-stattile__l">fuel boxes</div>
                    </div>
                    <div className="fgp-wz-stattile">
                      <div className="fgp-wz-stattile__v">{plan.stats.peakWeeklyMiles} mi</div>
                      <div className="fgp-wz-stattile__l">peak week</div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── Editable recap — catch mistakes before committing (F7) ── */}
          <div className="fgp-wz-recap">
            <div className="fgp-wz-recap__head">Your setup — tap anything to change it</div>
            {selectedRace && (
              <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("race")}>
                <span className="fgp-wz-recap__k">Event</span>
                <span className="fgp-wz-recap__v">
                  {selectedRace.name} · {formatYmdWeekdayShort(selectedRace.dateYmd)}
                  {selectedRace.distanceLabel ? ` · ${selectedRace.distanceLabel}` : ""} ✎
                </span>
              </button>
            )}
            {trainingMethodLabel == null ? (
              // Training question skipped (D18) — the calendar's workouts are the plan.
              <div className="fgp-wz-recap__row fgp-wz-recap__row--static">
                <span className="fgp-wz-recap__k">Training</span>
                <span className="fgp-wz-recap__v">Your planned workouts</span>
              </div>
            ) : (
              <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("method")}>
                <span className="fgp-wz-recap__k">Training</span>
                <span className="fgp-wz-recap__v">{trainingMethodLabel} ✎</span>
              </button>
            )}
            {/* Window — the plan-less journey's span (no length step on this
                path, so ✎ goes back to the race that sizes it). */}
            {isBuild && (
              <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("race")}>
                <span className="fgp-wz-recap__k">Window</span>
                <span className="fgp-wz-recap__v">{recapPlanSummary} ✎</span>
              </button>
            )}
            {!isBuild && (
              <>
                <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("goal")}>
                  <span className="fgp-wz-recap__k">Goal</span>
                  <span className="fgp-wz-recap__v">{goalLabel ?? "Any pace"} ✎</span>
                </button>
                <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("length")}>
                  <span className="fgp-wz-recap__k">Plan</span>
                  <span className="fgp-wz-recap__v">{recapPlanSummary} ✎</span>
                </button>
              </>
            )}
            {/* You → About your body. Allergens moved to Needs & tastes. */}
            <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("body")}>
              <span className="fgp-wz-recap__k">You</span>
              <span className="fgp-wz-recap__v">
                {[
                  fuel.sex ? `${fuel.sex[0].toUpperCase()}${fuel.sex.slice(1)}` : null,
                  fuel.weightText ? `${fuel.weightText} ${fuel.weightUnit}` : null,
                  fuel.sweat == null
                    ? "sweat not set"
                    : fuel.sweat === "lightly"
                      ? "low sodium"
                      : fuel.sweat === "pretzel"
                        ? "high sodium"
                        : "average sodium"
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {" ✎"}
              </span>
            </button>
            <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("fuel")}>
              <span className="fgp-wz-recap__k">Fueling</span>
              <span className="fgp-wz-recap__v">
                {recapFuelingSummary} ✎
              </span>
            </button>
            <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("needs")}>
              <span className="fgp-wz-recap__k">Needs &amp; tastes</span>
              <span className="fgp-wz-recap__v">
                {[
                  fuel.dietaryNeeds.size > 0
                    ? `${fuel.dietaryNeeds.size} allergen${fuel.dietaryNeeds.size === 1 ? "" : "s"}`
                    : fuel.optionsLoadFailed
                      ? "allergens not loaded"
                      : "no allergens",
                  fuel.flavorPrefs.size + fuel.flavorAvoids.size > 0
                    ? `${fuel.flavorPrefs.size + fuel.flavorAvoids.size} flavor picks`
                    : null
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {" ✎"}
              </span>
            </button>
            <button type="button" className="fgp-wz-recap__row" onClick={() => setStep("cadence")}>
              <span className="fgp-wz-recap__k">Boxes</span>
              <span className="fgp-wz-recap__v">
                {`Every ${dropWeeks === 1 ? "week" : `${dropWeeks} weeks`} · ${dropWindows.length} ${dropWindows.length === 1 ? "box" : "boxes"}`}
                {" ✎"}
              </span>
            </button>
          </div>

          {/* ── Projected race-day plan (design 1e) ── */}
          <div className="fgp-wz-racecard">
            <div className="fgp-wz-racecard__head">
              <span className="fgp-wz-racecard__title">
                <LucideIcon name="flag" size={16} color="var(--fg-purple)" />
                Your projected race-day plan
              </span>
              <span className="fgp-wz-racecard__chip">Included</span>
            </div>
            <div className="fgp-wz-racecard__stats">
              <div className="fgp-wz-racecard__stat">
                <div className="fgp-wz-racecard__v">{projectedRaceCarbs} g/hr</div>
                <div className="fgp-wz-racecard__l">Carbs during</div>
              </div>
              <div className="fgp-wz-racecard__stat">
                {/* W-17 — an explicit typed sodium wins over the sweat proxy. */}
                <div className="fgp-wz-racecard__v">{fuel.effectiveSodium} mg/hr</div>
                <div className="fgp-wz-racecard__l">Sodium during</div>
              </div>
              <div className="fgp-wz-racecard__stat">
                <div className="fgp-wz-racecard__v">{projectedRaceTotal} g</div>
                <div className="fgp-wz-racecard__l">Total carbs{goalLabel ? ` · ${goalLabel} goal` : ""}</div>
              </div>
            </div>
            <p className="fgp-wz-racecard__foot">
              Projected from your goal and profile — exact products get picked near race day,
              from what worked in training.
            </p>
          </div>

          {applyError && <div className="fgp-modal-callout fgp-modal-callout--error">{applyError}</div>}
          {overlapUnresolved && !applyError && (
            <div className="fgp-modal-callout fgp-modal-callout--amber" role="status">
              Your race overlaps an existing training plan — apply again and choose how they
              fit to finish adding this plan.
            </div>
          )}
        </div>
      )}
    </>
  );
}
